import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Loader2, ImagePlus, X, ChevronLeft, ChevronRight, Star } from "lucide-react";
import { Logo } from "@/components/Logo";
import { toast } from "@/components/ui/sonner";
import { imgUrl } from "@/pages/admin/PropertiesTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "h-12 w-full rounded-lg border border-line bg-warm/60 px-3.5 text-[14px] font-medium text-ink placeholder:text-ink-soft/60 focus:outline-none focus:ring-1 focus:ring-gold focus:border-gold transition";
const labelCls = "block text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-soft mb-2";

const EMPTY = {
  name: "", location: "", status: "Under Construction", propertyType: "Residential",
  shortDesc: "", startingPrice: "", bhkOptions: "", details: "",
  featured: false, published: false,
};

export default function ProjectForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [form, setForm] = useState(EMPTY);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(0);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  useEffect(() => {
    if (!isEdit) return;
    const token = localStorage.getItem("rk_admin_token");
    if (!token) return navigate("/admin/login");
    axios
      .get(`${API}/admin/projects`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        const p = data.find((x) => x.id === id);
        if (!p) {
          toast.error("Project not found.");
          return navigate("/admin?tab=projects");
        }
        const { id: _i, created_at: _c, updated_at: _u, ...rest } = p;
        setForm({ ...EMPTY, ...rest });
        setImages(p.images || []);
      })
      .catch(() => navigate("/admin/login"))
      .finally(() => setLoading(false));
  }, [id, isEdit, navigate]);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    const token = localStorage.getItem("rk_admin_token");
    for (const f of files) {
      setUploading((c) => c + 1);
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await axios.post(`${API}/admin/uploads`, fd, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setImages((imgs) => [...imgs, data.path]);
      } catch (err) {
        toast.error(err?.response?.data?.detail || `Could not upload ${f.name}.`);
      } finally {
        setUploading((c) => c - 1);
      }
    }
  };

  const move = (i, dir) => {
    setImages((imgs) => {
      const j = i + dir;
      if (j < 0 || j >= imgs.length) return imgs;
      const next = [...imgs];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const save = async (publish) => {
    if (form.name.trim().length < 3) return toast.error("Project name is required.");
    if (!form.location.trim()) return toast.error("Location is required.");
    const token = localStorage.getItem("rk_admin_token");
    setSaving(true);
    const payload = {
      ...form,
      name: form.name.trim(),
      location: form.location.trim(),
      images,
      published: publish ?? form.published,
    };
    try {
      if (isEdit) {
        await axios.put(`${API}/admin/projects/${id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API}/admin/projects`, payload, { headers: { Authorization: `Bearer ${token}` } });
      }
      toast.success(
        payload.published
          ? payload.featured ? "Project published — featured on the homepage." : "Project published."
          : "Project saved as draft."
      );
      navigate("/admin?tab=projects");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return <div className="min-h-screen bg-warm flex items-center justify-center text-ink-soft"><Loader2 className="animate-spin" size={28} /></div>;

  return (
    <div className="min-h-screen bg-warm" data-testid="project-form-page">
      <header className="bg-navy sticky top-0 z-40">
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 h-[68px] flex items-center justify-between">
          <Link to="/admin?tab=projects" data-testid="project-form-logo"><Logo dark /></Link>
          <Link to="/admin?tab=projects" className="text-[13px] text-white/60 hover:text-gold transition-colors inline-flex items-center gap-1.5" data-testid="project-form-back">
            <ArrowLeft size={14} /> Back to Projects
          </Link>
        </div>
      </header>

      <main className="max-w-[900px] mx-auto px-4 sm:px-6 py-10">
        <h1 className="font-display text-2xl sm:text-3xl font-semibold text-navy">
          {isEdit ? "Edit Project" : "Add New Project"}
        </h1>
        <p className="mt-1.5 text-sm text-ink-soft">
          Mark a project Featured + Published to show it in the homepage Featured Projects section.
        </p>

        <div className="mt-8 bg-white rounded-xl border border-line p-6 sm:p-8 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>Project Name*</label>
              <input className={inputCls} placeholder="e.g. RK Green Heights" value={form.name} onChange={set("name")} data-testid="pjf-name" />
            </div>
            <div>
              <label className={labelCls}>Location*</label>
              <input className={inputCls} placeholder="e.g. Alwar Bypass Road, Bhiwadi" value={form.location} onChange={set("location")} data-testid="pjf-location" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>Project Status*</label>
              <select className={inputCls} value={form.status} onChange={set("status")} data-testid="pjf-status">
                {["Under Construction", "Ready to Move", "Upcoming"].map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Property Type</label>
              <select className={inputCls} value={form.propertyType} onChange={set("propertyType")} data-testid="pjf-type">
                {["Residential", "Commercial", "Plots", "Mixed"].map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>Starting Price</label>
              <input className={inputCls} placeholder="e.g. ₹ 42 Lakh onwards" value={form.startingPrice} onChange={set("startingPrice")} data-testid="pjf-price" />
            </div>
            <div>
              <label className={labelCls}>BHK Options</label>
              <input className={inputCls} placeholder="e.g. 2, 3 BHK" value={form.bhkOptions} onChange={set("bhkOptions")} data-testid="pjf-bhk" />
            </div>
          </div>

          <div>
            <label className={labelCls}>Short Description</label>
            <textarea rows={2} placeholder="One or two lines shown on the homepage card..." value={form.shortDesc} onChange={set("shortDesc")}
              className="w-full rounded-lg border border-line bg-warm/60 px-3.5 py-3 text-[14px] font-medium text-ink placeholder:text-ink-soft/60 focus:outline-none focus:ring-1 focus:ring-gold focus:border-gold transition resize-none"
              data-testid="pjf-short-desc" />
          </div>

          <div>
            <label className={labelCls}>Project Details</label>
            <textarea rows={5} placeholder="Full details — amenities, towers, possession, approvals..." value={form.details} onChange={set("details")}
              className="w-full rounded-lg border border-line bg-warm/60 px-3.5 py-3 text-[14px] font-medium text-ink placeholder:text-ink-soft/60 focus:outline-none focus:ring-1 focus:ring-gold focus:border-gold transition resize-none"
              data-testid="pjf-details" />
          </div>

          <div>
            <label className={labelCls}>Project Images</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="pjf-images">
              {images.map((path, i) => (
                <div key={path} className="relative group aspect-[4/3] rounded-lg overflow-hidden border border-line bg-warm" data-testid={`pjf-image-${i}`}>
                  <img src={imgUrl(path)} alt={`Upload ${i + 1}`} className="w-full h-full object-cover" />
                  {i === 0 && <span className="absolute top-2 left-2 bg-gold text-navy text-[9px] font-bold tracking-wider px-2 py-0.5 rounded">COVER</span>}
                  <div className="absolute inset-x-0 bottom-0 flex justify-center gap-1 p-1.5 bg-navy/70 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="p-1.5 text-white disabled:opacity-30" aria-label="Move left"><ChevronLeft size={15} /></button>
                    <button type="button" onClick={() => setImages((imgs) => imgs.filter((_, x) => x !== i))} className="p-1.5 text-red-300" aria-label="Delete image" data-testid={`pjf-image-delete-${i}`}><X size={15} /></button>
                    <button type="button" onClick={() => move(i, 1)} disabled={i === images.length - 1} className="p-1.5 text-white disabled:opacity-30" aria-label="Move right"><ChevronRight size={15} /></button>
                  </div>
                </div>
              ))}
              <label className="aspect-[4/3] rounded-lg border-2 border-dashed border-line hover:border-gold flex flex-col items-center justify-center gap-2 cursor-pointer text-ink-soft hover:text-navy transition-colors" data-testid="pjf-upload-trigger">
                {uploading > 0 ? <Loader2 size={20} className="animate-spin" /> : <ImagePlus size={20} />}
                <span className="text-[11px] font-semibold">{uploading > 0 ? "Uploading..." : "Add Images"}</span>
                <input type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={onFiles} data-testid="pjf-upload-input" />
              </label>
            </div>
            <p className="mt-2 text-[12px] text-ink-soft">JPG, PNG or WebP, up to 8 MB each. First image becomes the cover. Hover an image to reorder or delete.</p>
          </div>

          <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="pjf-featured">
            <input type="checkbox" checked={form.featured} onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))} className="h-5 w-5 accent-[#C9A45C]" />
            <span className="text-sm font-medium text-ink inline-flex items-center gap-1.5"><Star size={14} className="text-gold" /> Featured project (shows in the homepage Featured Projects section)</span>
          </label>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <button onClick={() => save(false)} disabled={saving}
            className="flex-1 border border-navy/25 text-navy hover:border-gold transition-colors rounded-lg px-6 py-3.5 text-[15px] font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="pjf-save-draft">
            {saving ? <Loader2 size={16} className="animate-spin" /> : isEdit && form.published ? "Save (Keep Published)" : "Save as Draft"}
          </button>
          <button onClick={() => save(true)} disabled={saving}
            className="flex-1 bg-gold text-navy hover:bg-gold-dark transition-colors rounded-lg px-6 py-3.5 text-[15px] font-semibold shadow-md shadow-gold/25 disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="pjf-save-publish">
            {saving ? <Loader2 size={16} className="animate-spin" /> : "Save & Publish"}
          </button>
        </div>
      </main>
    </div>
  );
}
